import { useEffect, useState } from "react";
import { Send, ImagePlus, X } from "lucide-react";
import { api, photoUrl } from "../../lib/api";
import { PLATFORM_META, fmt } from "./platformMeta";

export function Composer({ accounts, posts, onPost }) {
  const connected = accounts.filter((a) => a.connected);
  const [content, setContent] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [photoIds, setPhotoIds] = useState([]);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data || [])).catch(() => setJobs([]));
  }, []);

  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const togglePhoto = (id) =>
    setPhotoIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < 4 ? [...s, id] : s));

  const submit = async (e) => {
    e.preventDefault();
    if (!selected.length) { setMsg({ kind: "error", text: "Select at least one platform." }); return; }
    setBusy(true);
    const res = await onPost(content, selected, photoIds);
    setBusy(false);
    if (res.ok) {
      setContent("");
      setSelected([]);
      setPhotoIds([]);
      setPickerOpen(false);
      setMsg({ kind: "ok", text: `Posted to ${selected.length} platform${selected.length > 1 ? "s" : ""} (demo).` });
    } else {
      setMsg({ kind: "error", text: res.error });
    }
  };

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-8" data-testid="social-composer">
      <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">Compose a Post</div>
      <p className="mt-1 text-sm text-[#23282B]/60">Write once — post to every platform, or pick individual ones.</p>

      <form onSubmit={submit} className="mt-5">
        <textarea
          data-testid="social-post-content"
          required
          rows={4}
          maxLength={3000}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="e.g. Fresh stamped-concrete patio wrapped up in Duncan today — swipe for the before shots."
          className="w-full border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3 resize-y"
        />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#23282B]/60 mr-1">Post to:</span>
          {connected.length === 0 && <span className="text-sm text-[#23282B]/50">Connect a platform above first.</span>}
          {connected.map((a) => {
            const meta = PLATFORM_META[a.platform];
            const on = selected.includes(a.platform);
            return (
              <button
                type="button"
                key={a.platform}
                onClick={() => toggle(a.platform)}
                data-testid={`social-post-target-${a.platform}`}
                className={`inline-flex items-center gap-2 text-xs font-bold tracking-[0.1em] uppercase px-3 py-2 border-2 transition-colors ${on ? "text-white border-transparent" : "text-[#23282B] border-[#23282B]/20 hover:border-[#23282B]/50"}`}
                style={on ? { background: meta.color } : undefined}
              >
                <i className={meta.icon} /> {meta.name}
              </button>
            );
          })}
          {connected.length > 1 && (
            <button type="button" data-testid="social-post-target-all" onClick={() => setSelected(connected.map((a) => a.platform))} className="text-xs font-bold tracking-[0.1em] uppercase px-3 py-2 border-2 border-dashed border-[#2F5F80] text-[#2F5F80] hover:bg-[#9CC4DB]/10 transition-colors">
              All Platforms
            </button>
          )}
        </div>

        {/* Photo attachments from Past Jobs gallery */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            data-testid="social-photo-picker-toggle"
            className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.14em] uppercase border-2 border-[#23282B]/20 hover:border-[#2F5F80] hover:text-[#2F5F80] text-[#23282B] px-3 py-2 transition-colors"
          >
            <ImagePlus size={14} /> Attach Photos from Past Jobs {photoIds.length > 0 && `(${photoIds.length}/4)`}
          </button>

          {photoIds.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="social-photo-selected">
              {photoIds.map((id) => (
                <div key={id} className="relative">
                  <img src={photoUrl(id)} alt="attachment" className="w-16 h-16 object-cover border-2 border-[#2F5F80]" />
                  <button type="button" onClick={() => togglePhoto(id)} data-testid={`social-photo-remove-${id}`} className="absolute -top-1.5 -right-1.5 bg-[#23282B] hover:bg-red-600 text-white p-0.5" aria-label="Remove photo">
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {pickerOpen && (
            <div className="mt-3 border-2 border-[#23282B]/10 bg-[#F1F3F5] p-3" data-testid="social-photo-picker">
              {jobs.length === 0 ? (
                <div className="text-sm text-[#23282B]/60 p-3">No gallery photos yet — upload some in the "Upload Job" tab first.</div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {jobs.map((j) => {
                    const on = photoIds.includes(j.id);
                    return (
                      <button
                        type="button"
                        key={j.id}
                        onClick={() => togglePhoto(j.id)}
                        data-testid={`social-photo-option-${j.id}`}
                        title={j.job_site_name}
                        className={`relative aspect-square overflow-hidden border-2 transition-colors ${on ? "border-[#2F5F80]" : "border-transparent hover:border-[#2F5F80]"}`}
                      >
                        <img src={photoUrl(j.id)} alt={j.job_site_name} className="w-full h-full object-cover" loading="lazy" />
                        {on && <span className="absolute inset-0 bg-[#2F5F80]/30" />}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="mt-2 text-[11px] text-[#23282B]/50">Up to 4 photos per post.</div>
            </div>
          )}
        </div>

        {msg && (
          <div data-testid={`social-post-${msg.kind}`} className={`mt-4 px-4 py-3 text-sm border-l-4 ${msg.kind === "ok" ? "bg-green-50 border-green-600 text-green-800" : "bg-red-50 border-red-500 text-red-700"}`}>
            {msg.text}
          </div>
        )}

        <button type="submit" disabled={busy || connected.length === 0} data-testid="social-post-submit" className="mt-5 inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-7 py-3.5 uppercase transition-colors disabled:opacity-50">
          <Send size={16} /> {busy ? "Posting…" : "Publish Post"}
        </button>
      </form>

      {posts.length > 0 && (
        <div className="mt-8 pt-6 border-t border-[#23282B]/10">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#2F5F80] mb-3">Recent Posts</div>
          <div className="space-y-3" data-testid="social-posts-list">
            {posts.slice(0, 5).map((p) => (
              <article key={p.id} className="border-2 border-[#23282B]/10 p-4" data-testid={`social-post-${p.id}`}>
                <p className="text-sm text-[#23282B]/90 leading-relaxed">{p.content}</p>
                {p.photo_ids?.length > 0 && (
                  <div className="mt-2 flex gap-2">
                    {p.photo_ids.map((id) => (
                      <img key={id} src={photoUrl(id)} alt="post attachment" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-14 h-14 object-cover border border-[#23282B]/15" loading="lazy" />
                    ))}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#23282B]/60">
                  {p.platforms.map((key) => (
                    <span key={key} className="inline-flex items-center gap-1.5">
                      <i className={PLATFORM_META[key].icon} style={{ color: PLATFORM_META[key].color }} />
                      {fmt(p.metrics?.[key]?.views)} views · {fmt(p.metrics?.[key]?.likes)} likes
                    </span>
                  ))}
                  <span className="ml-auto">{p.created_at?.slice(0, 10)}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
